import { useEffect, useRef, useState } from "react";

const IDLE = { status: "idle", message: "", resolved: null };
const DEBOUNCE_MS = 250;

async function lookupPincode(code) {
  const res = await fetch(`https://api.postalpincode.in/pincode/${code}`);
  const data = await res.json();
  const entry = Array.isArray(data) ? data[0] : null;
  if (!entry || entry.Status !== "Success" || !entry.PostOffice?.length) return null;
  const po = entry.PostOffice[0];
  return { pincode: code, city: po.Name || po.District, district: po.District, state: po.State, isRajasthan: (po.State || "").toLowerCase() === "rajasthan" };
}

function stateFor(resolved) {
  if (!resolved) return { status: "error", message: "Pincode not found in India Post directory.", resolved: null };
  if (!resolved.isRajasthan) return { status: "error", message: `Only Rajasthan pincodes are supported (found ${resolved.state}).`, resolved };
  return { status: "ok", message: `${resolved.city}, ${resolved.district}, ${resolved.state}`, resolved };
}

/* Debounced India Post lookup (Rajasthan-only). Returns { pin, state, handleInput }. */
export default function usePincodeLookup({ value, onChange, onResolved }) {
  const [state, setState] = useState(IDLE);
  const [pin, setPin] = useState(value || "");
  const debounceRef = useRef(null);

  useEffect(() => { setPin(value || ""); }, [value]);
  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const runLookup = async (code) => {
    if (!/^\d{6}$/.test(code)) { setState(IDLE); return; }
    setState({ status: "loading", message: "Looking up…", resolved: null });
    try {
      const resolved = await lookupPincode(code);
      setState(stateFor(resolved));
      if (resolved) onResolved?.(resolved);
    } catch {
      setState({ status: "error", message: "Lookup failed. Check network.", resolved: null });
    }
  };

  const handleInput = (e) => {
    const raw = e.target.value.replace(/\D/g, "").slice(0, 6);
    setPin(raw);
    onChange?.(raw);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runLookup(raw), DEBOUNCE_MS);
  };

  return { pin, state, handleInput };
}
